export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_SUSPENDED = 'ACCOUNT_SUSPENDED',
  RATE_LIMITED = 'RATE_LIMITED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  MFA_INVALID = 'MFA_INVALID'
}

export interface AuthError {
  type: AuthErrorType;
  message: string;
  retryable: boolean;
  requiresAction: boolean;
}

export function classifyAuthError(error: any): AuthError {
  // Network errors
  if (error?.isNetwork) {
    return {
      type: AuthErrorType.NETWORK_ERROR,
      message: 'Network connection failed. Please check your internet connection.',
      retryable: true,
      requiresAction: false
    };
  }

  // Timeout errors
  if (error?.isTimeout) {
    return {
      type: AuthErrorType.TIMEOUT_ERROR,
      message: 'Request timed out. Please try again.',
      retryable: true,
      requiresAction: false
    };
  }

  // Handle MFA required response from loginApi
  if (error?.success === false && error?.message === "MFA verification required" && error?.data?.mfaRequired) {
    return {
      type: AuthErrorType.MFA_REQUIRED,
      message: 'Please enter your 6-digit authentication code',
      retryable: false,
      requiresAction: true
    };
  }

  // Handle API response format (fallback)
  if (error?.message === "MFA verification required") {
    return {
      type: AuthErrorType.MFA_REQUIRED,
      message: 'Please enter your 6-digit authentication code',
      retryable: false,
      requiresAction: true
    };
  }

  if (error?.error?.code === "UNAUTHORIZED" && error?.error?.message === "Invalid credentials") {
    return {
      type: AuthErrorType.INVALID_CREDENTIALS,
      message: 'Invalid email or password',
      retryable: false,
      requiresAction: false
    };
  }

  if (error?.error?.code === "UNAUTHORIZED" && error?.error?.message === "Invalid MFA code") {
    return {
      type: AuthErrorType.MFA_INVALID,
      message: 'Invalid authentication code. Please check and try again.',
      retryable: false,
      requiresAction: false
    };
  }

  // HTTP status based errors
  const status = error?.status;

  switch (status) {
    case 401:
      // Check if it's specifically about MFA
      if (error?.data?.mfaRequired) {
        return {
          type: AuthErrorType.MFA_REQUIRED,
          message: 'Please enter your 6-digit authentication code',
          retryable: false,
          requiresAction: true
        };
      }
      return {
        type: AuthErrorType.INVALID_CREDENTIALS,
        message: 'Invalid email or password',
        retryable: false,
        requiresAction: false
      };

    case 403:
      if (error?.detail?.code === 'ACCOUNT_LOCKED') {
        return {
          type: AuthErrorType.ACCOUNT_LOCKED,
          message: 'Account has been locked due to multiple failed attempts. Please contact support.',
          retryable: false,
          requiresAction: true
        };
      }
      if (error?.detail?.code === 'ACCOUNT_SUSPENDED') {
        return {
          type: AuthErrorType.ACCOUNT_SUSPENDED,
          message: 'Account has been suspended. Please contact support.',
          retryable: false,
          requiresAction: true
        };
      }
      return {
        type: AuthErrorType.SERVER_ERROR,
        message: 'Access denied. Please contact support if this persists.',
        retryable: false,
        requiresAction: true
      };

    case 429:
      return {
        type: AuthErrorType.RATE_LIMITED,
        message: 'Too many login attempts. Please wait a few minutes before trying again.',
        retryable: true,
        requiresAction: false
      };

    case 500:
    case 502:
    case 503:
      return {
        type: AuthErrorType.SERVER_ERROR,
        message: 'Server is temporarily unavailable. Please try again later.',
        retryable: true,
        requiresAction: false
      };

    default:
      return {
        type: AuthErrorType.SERVER_ERROR,
        message: error?.message || 'An unexpected error occurred. Please try again.',
        retryable: true,
        requiresAction: false
      };
  }
}

export function getErrorAction(error: AuthError): string | null {
  switch (error.type) {
    case AuthErrorType.INVALID_CREDENTIALS:
      return 'Check your email and password and try again.';

    case AuthErrorType.MFA_REQUIRED:
      return 'Enter the 6-digit code from your authenticator app.';

    case AuthErrorType.MFA_INVALID:
      return 'Please verify the code and try again.';

    case AuthErrorType.NETWORK_ERROR:
    case AuthErrorType.TIMEOUT_ERROR:
      return 'Check your internet connection and try again.';

    case AuthErrorType.RATE_LIMITED:
      return 'Please wait before trying again or contact support.';

    case AuthErrorType.ACCOUNT_LOCKED:
    case AuthErrorType.ACCOUNT_SUSPENDED:
      return 'Contact support to resolve this issue.';

    case AuthErrorType.SERVER_ERROR:
      return 'If this persists, please contact support.';

    default:
      return null;
  }
}